import { Flex, Grid, StaticCard } from "shared/components/Spor";
import { FC, useRef } from "react";
import { useTheme } from "styled-components";
import { MeasureOverviewHeader } from "features/CenterContent/RoleContent/AlternativeTransport/MeasureOverview/MeasureOverviewHeader";
import {
  MeasureOverviewContext,
  useMeasureOverviewContextValues,
} from "features/CenterContent/RoleContent/AlternativeTransport/MeasureOverview/MeasureOverviewContext";
import { useAlternativeTransportPlans } from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/useAlternativeTransportPlans";
import { PLAN_MEASURE_KEYS } from "features/CenterContent/RoleContent/AlternativeTransport/MeasureOverview/types";
import { MeasureOverviewColumn } from "features/CenterContent/RoleContent/AlternativeTransport/MeasureOverview/MeasureOverviewColumn/MeasureOverviewColumn";
import { MeasureOverviewColumnHeader } from "features/CenterContent/RoleContent/AlternativeTransport/MeasureOverview/MeasureOverviewColumn/MeasureOverviewColumnHeader";
import { LargeErrorState } from "shared/components/states/LargeErrorState";
import { LargeLoadingState } from "shared/components/states/LargeLoadingState";
import {
  ColumnSeverityContext,
  useColumnSeverityContextValues,
} from "features/CenterContent/RoleContent/AlternativeTransport/MeasureOverview/ColumnSeverityContext";
import { useNavigate } from "react-router-dom";

export const MeasureOverview: FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const contextValues = useMeasureOverviewContextValues();
  const columnSeverityValues = useColumnSeverityContextValues();

  const scrollVisibilityRootRef = useRef<HTMLDivElement | null>(null);

  const { data: plansResponse, isPending: arePlansLoading } =
    useAlternativeTransportPlans();

  return (
    <MeasureOverviewContext.Provider value={contextValues}>
      <StaticCard
        backgroundColor="semantics.colorBackgroundMain"
        border={`1px solid ${theme.colorOutlineSecondary}`}
        padding={3}
        height="100%"
        borderRadius="xs"
        display="grid"
        gridTemplateRows="auto 1fr"
        gap={3}
      >
        <MeasureOverviewHeader />

        {arePlansLoading && <LargeLoadingState />}

        {!arePlansLoading && !plansResponse && (
          <LargeErrorState onRetry={() => navigate(0)} />
        )}

        {!arePlansLoading && plansResponse && (
          <Flex containerType="size" overflowX="auto">
            <ColumnSeverityContext.Provider value={columnSeverityValues}>
              <Grid
                gridTemplateColumns="repeat(4, 1fr)"
                gridTemplateRows="auto 1fr"
                columnGap={2}
                minWidth="1452px"
                width="100%"
              >
                <Grid gridColumn="1 / -1" templateColumns="subgrid">
                  {PLAN_MEASURE_KEYS.map((measureKey) => (
                    <MeasureOverviewColumnHeader
                      key={measureKey}
                      measureKey={measureKey}
                      trainMeasures={plansResponse?.data?.train[measureKey]}
                      infraMeasures={
                        plansResponse?.data?.infrastructure[measureKey]
                      }
                    />
                  ))}
                </Grid>

                {/** This subgrid layout enables all columns to scroll in sync */}
                <Grid
                  ref={scrollVisibilityRootRef}
                  gridColumn="1 / -1"
                  templateColumns="subgrid"
                  overflowY="scroll"
                  gridTemplateRows="0 [content-start] min-content auto [content-end] 0"
                >
                  {PLAN_MEASURE_KEYS.map((measureKey) => (
                    <MeasureOverviewColumn
                      key={measureKey}
                      measureKey={measureKey}
                      trainMeasures={plansResponse?.data?.train[measureKey]}
                      infraMeasures={
                        plansResponse?.data?.infrastructure[measureKey]
                      }
                      rootRef={scrollVisibilityRootRef}
                    />
                  ))}
                </Grid>
              </Grid>
            </ColumnSeverityContext.Provider>
          </Flex>
        )}
      </StaticCard>
    </MeasureOverviewContext.Provider>
  );
};
